import { Injectable, inject } from '@angular/core';
import { forkJoin, map, Observable, switchMap } from 'rxjs';
import { AdminApiService } from './admin-api.service';
import { AdminMapperService } from './admin-mapper.service';
import { RebookShowOption, ShowDetail, ShowListItem } from '../models/show.model';
import { AdminReservationUpdateRequestDto, AdminShowInformationDto } from '../models/admin-api.model';
import { AdminRebookMapperService } from './admin-rebook-mapper.service';
import { environment } from '../../../environments/environment';

@Injectable({
    providedIn: 'root'
})
export class ShowsService {
    private readonly adminApi = inject(AdminApiService);
    private readonly mapper = inject(AdminMapperService);
    private readonly rebookMapper = inject(AdminRebookMapperService);

    private readonly adminShowsRangeInDays = environment.adminShowsRangeInDays ?? 14;
    private readonly adminInitialShowsRangeInDays = 7;

    getShows(dateStartUtc: string, dateEndUtc: string): Observable<ShowListItem[]> {
        return forkJoin({
            info: this.adminApi.getMusicalInfo(),
            showsResponse: this.adminApi.getShows(dateStartUtc, dateEndUtc),
            reservations: this.adminApi.getReservations(this.getTodayStartUtc())
        }).pipe(
            map(({ info, showsResponse, reservations }) =>
                this.mapper.toShowListItems(showsResponse.showInformations ?? [], reservations, info)
            )
        );
    }

    getShowDetail(showId: number, dateStartUtc: string, dateEndUtc: string): Observable<ShowDetail | undefined> {
        return forkJoin({
            info: this.adminApi.getMusicalInfo(),
            showsResponse: this.adminApi.getShows(dateStartUtc, dateEndUtc),
            reservations: this.adminApi.getReservations(this.getTodayStartUtc())
        }).pipe(
            map(({ info, showsResponse, reservations }) => {
                const show = (showsResponse.showInformations ?? []).find(
                    (item: AdminShowInformationDto) => item.showID === showId
                );

                if (!show) {
                    return undefined;
                }

                return this.mapper.toShowDetail(show, reservations, info);
            })
        );
    }

    rebookReservation(payload: AdminReservationUpdateRequestDto) {
        return this.adminApi.updateReservation(payload);
    }

    cancelReservation(
        processId: string,
        showId: number,
        dateStartUtc: string,
        dateEndUtc: string,
        cancelledReason?: string
    ): Observable<ShowDetail | undefined> {
        return this.adminApi.cancelReservation(processId, cancelledReason).pipe(
            switchMap(() => this.getShowDetail(showId, dateStartUtc, dateEndUtc))
        );
    }

    getRebookShowOptions(dateStartUtc: string, dateEndUtc: string): Observable<RebookShowOption[]> {
        return forkJoin({
            info: this.adminApi.getMusicalInfo(),
            showsResponse: this.adminApi.getShows(dateStartUtc, dateEndUtc)
        }).pipe(
            map(({ info, showsResponse }) =>
                this.rebookMapper.toRebookShowOptions(
                    showsResponse.showInformations ?? [],
                    info.musicalName?.trim() || 'Show'
                )
            )
        );
    }

    getTodayStartUtc(baseDate: Date = new Date()): string {
        return new Date(Date.UTC(
            baseDate.getUTCFullYear(),
            baseDate.getUTCMonth(),
            baseDate.getUTCDate(),
            0,
            0,
            0,
            0
        )).toISOString();
    }

    getUtcDateRange(daysAhead: number, baseDate: Date = new Date()): { startUtc: string; endUtc: string } {
        const startUtc = new Date(Date.UTC(
            baseDate.getUTCFullYear(),
            baseDate.getUTCMonth(),
            baseDate.getUTCDate(),
            0,
            0,
            0,
            0
        )).toISOString();

        const endUtc = new Date(Date.UTC(
            baseDate.getUTCFullYear(),
            baseDate.getUTCMonth(),
            baseDate.getUTCDate() + daysAhead,
            23,
            59,
            59,
            999
        )).toISOString();

        return { startUtc, endUtc };
    }

    getInitialAdminRange(baseDate: Date = new Date()): { startUtc: string; endUtc: string } {
        return this.getUtcDateRange(this.adminInitialShowsRangeInDays, baseDate);
    }

    getMaxFilterAdminRange(baseDate: Date = new Date()): { startUtc: string; endUtc: string } {
        return this.getUtcDateRange(this.adminShowsRangeInDays, baseDate);
    }

    getSingleDayRange(selectedDate: Date): { startUtc: string; endUtc: string } {
        const startUtc = new Date(Date.UTC(
            selectedDate.getFullYear(),
            selectedDate.getMonth(),
            selectedDate.getDate(),
            0,
            0,
            0,
            0
        )).toISOString();

        const endUtc = new Date(Date.UTC(
            selectedDate.getFullYear(),
            selectedDate.getMonth(),
            selectedDate.getDate(),
            23,
            59,
            59,
            999
        )).toISOString();

        return { startUtc, endUtc };
    }

    getShowDetailPageData(showId: number, dateStartUtc: string, dateEndUtc: string) {
        return forkJoin({
            info: this.adminApi.getMusicalInfo(),
            showsResponse: this.adminApi.getShows(dateStartUtc, dateEndUtc),
            reservations: this.adminApi.getReservations(this.getTodayStartUtc())
        }).pipe(
            map(({ info, showsResponse, reservations }) => {
                const shows = showsResponse.showInformations ?? [];
                const show = shows.find(item => item.showID === showId);

                return {
                    info,
                    detail: show ? this.mapper.toShowDetail(show, reservations, info) : undefined,
                    rebookOptions: this.rebookMapper.toRebookShowOptions(
                        shows,
                        info.musicalName?.trim() || 'Show'
                    )
                };
            })
        );
    }

    getShowsPageData(dateStartUtc: string, dateEndUtc: string) {
        return forkJoin({
            info: this.adminApi.getMusicalInfo(),
            showsResponse: this.adminApi.getShows(dateStartUtc, dateEndUtc),
            reservations: this.adminApi.getReservations(this.getTodayStartUtc())
        }).pipe(
            map(({ info, showsResponse, reservations }) => ({
                info,
                shows: this.mapper.toShowListItems(showsResponse.showInformations ?? [], reservations, info)
            }))
        );
    }
}